/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  Pipe,
  PipeTransform,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { SearchComponent } from '../../molecules/search/search.component';
import InputType from '../../../shared/enums/input-type.enums';
import { ButtonType } from '../../../shared/enums/button-type.enums';
import { from, Observable, Subscription } from 'rxjs';
import { SearchGroup } from '../../../shared/interfaces/search-group.interface';

// This pipe is used to group an array of objects by a specific property
@Pipe({ name: 'groupBy', standalone: true })
export class GroupByPipe implements PipeTransform {
  transform(array: any[], property: string): any[] {
    if (!Array.isArray(array) || !property) return array;
    const grouped = array.reduce(
      (acc, item) => {
        const key = item[property] || 'Unbenannte Sektion';
        acc[key] = acc[key] || [];
        acc[key].push(item);
        return acc;
      },
      {} as { [key: string]: any[] }
    );

    return Object.keys(grouped).map((key) => ({
      key,
      value: grouped[key],
    }));
  }
}
@Component({
  selector: 'hdi-inpage-search',
  standalone: true,
  imports: [CommonModule, SearchComponent, GroupByPipe],
  templateUrl: './inpage-search.component.html',
  styleUrl: './inpage-search.component.scss',
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class InpageSearchComponent implements OnDestroy {
  @Input() placeholder: string = 'Search';
  @Input() disabled: boolean = false;
  @Input() CTA: boolean = false;
  @Input() labelCTA: string = '';
  @Input() labelCTAMobile: string = this.labelCTA;
  @Input() flexDirection: string = 'row';
  @Input() flexDirectionMobile: string = this.flexDirection;
  @Input() buttonType: ButtonType = ButtonType.SQUARED_PRIMARY;
  @Input() buttonTypeMobile: ButtonType = this.buttonType;
  @Input() buttonIcon: string = 'arrow-right';
  @Input() buttonIconMobile: string = this.buttonIcon;
  @Input() expandButton: boolean = false;
  @Input() padding: boolean = true;
  @Input() searchGroup?: SearchGroup[];
  @Output() handleInputChange: EventEmitter<string> =
    new EventEmitter<string>();
  inputType = InputType.Default;
  buttonDisabled: boolean = true;
  searchedElements: SearchGroup[] = [];
  private getSearchedElementSubs$: Subscription = new Subscription();
  private readonly resetPreviousSearchedSubs$: Subscription =
    new Subscription();
  public hasError: boolean = false;
  public isEmpty: boolean = true;
  public results: string = '';
  @ViewChild(SearchComponent, { static: false })
  searchComponent?: SearchComponent;
  @ViewChild('resultContainer') resultContainer: ElementRef<HTMLUListElement> =
    {} as ElementRef<HTMLUListElement>;

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2
  ) {}

  // Handle search result click
  inputChange(event: string) {
    this.buttonDisabled = event.length === 0;
    this.handleInputChange.emit(event);
    this.removeHighlight();
    if (event.length >= 3) {
      this.hasError = false;
      this.getSearchedElementSubs$ = this.getSearchedElements().subscribe(
        (elements) => {
          this.searchedElements = elements.map((el) => ({
            ...el,
            headline: el.label,
            key: el.anchor,
            searched: [],
          }));
        }
      );
    } else if (event.length < 3) {
      this.hasError = true;
      this.searchedElements = [];
    }

    if (event.length === 0) {
      this.hasError = false;
      this.isEmpty = true;
    } else {
      this.isEmpty = false;
    }
  }

  private getSearchedElements(): Observable<
    { label: string; anchor: string; sectionHeadline: string }[]
  > {
    if (!this.searchComponent?.inputElement) {
      console.error('Search input not found');
      return from([[]]);
    }

    const searchValue = this.searchComponent.inputElement.value
      .trim()
      .toLowerCase();

    if (!searchValue) {
      return from([[]]);
    }

    // Find the section containing the search input
    const inputSection = this.searchComponent.inputElement.closest('section');

    // Query all elements inside <section> tags, but skip the section containing the input
    const sections = Array.from(
      this.document.querySelectorAll('section')
    ).filter((section) => section !== inputSection);

    const elements: { el: HTMLElement; section: HTMLElement }[] = [];
    sections.forEach((section) => {
      const sectionElements = Array.from(section.querySelectorAll('*'));
      sectionElements.forEach((el) =>
        elements.push({ el: el as HTMLElement, section })
      );
    });

    const seen = new Set<string>();

    const results = elements
      .filter(({ el }) => {
        const text = el.textContent?.toLowerCase() ?? '';
        const match = text.includes(searchValue);
        return match;
      })
      .map(({ el, section }) => {
        const headlineEl = section.querySelector('h1, h2, h3, h4, h5, h6');
        const sectionHeadline =
          headlineEl?.textContent?.trim() ?? 'Unbenannte Sektion';

        let anchor = el.id;
        if (!anchor) {
          const words = this.getOwnText(el)
            .toLowerCase()
            .split(/\s+/)
            .slice(0, 3)
            .join('-')
            .replace(/[^a-z0-9-]/g, '');
          anchor = `anchor-${words}`;
          el.id = anchor;
        }

        // Use only the element's own text for the label, but show the part containing the search term
        let label = this.getOwnText(el);
        const searchIndex = label.toLowerCase().indexOf(searchValue);
        if (searchIndex !== -1) {
          // Show up to 10 chars before and after the match for context
          const context = 20;
          const start = Math.max(0, searchIndex - context);
          const end = Math.min(
            label.length,
            searchIndex + searchValue.length + context
          );
          label =
            (start > 0 ? '…' : '') +
            label.substring(start, end).trim() +
            (end < label.length ? '…' : '');
        } else {
          // fallback: first 3 words
          label = label.split(/\s+/).slice(0, 3).join(' ');
        }

        return {
          label,
          anchor: `#${anchor}`,
          sectionHeadline,
        };
      })
      .filter((item) => {
        if (seen.has(item.label + item.sectionHeadline)) {
          return false;
        }
        seen.add(item.label + item.sectionHeadline);
        return true;
      });

    return from([results]);
  }

  private getOwnText(el: HTMLElement): string {
    let ownText = '';
    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        ownText += node.textContent;
      }
    }
    return ownText.trim();
  }
  handleKeyDown(event: KeyboardEvent, anchor: string) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.scrollToResult(anchor);
    }
  }
  scrollToResult(anchor: string) {
    const anchorId = anchor.startsWith('#') ? anchor.substring(1) : anchor;
    const el = this.document.getElementById(anchorId);
    if (!el) {
      console.warn('Element not found for anchor:', anchorId);
      this.closeSearchResults();
      return;
    }
    // Try to find the closest accordion parent
    const accordion = el.closest('.accordion');
    if (accordion) {
      // If the accordion is not open, open it
      const accordionBtn = accordion.querySelector(
        '.accordion_btn'
      ) as HTMLElement;
      if (accordionBtn && !accordion.classList.contains('active')) {
        accordionBtn.click();
      }
      // Wait for the accordion to open before scrolling
      setTimeout(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus?.();
        el.classList.add('highlighted');
      });
    } else {
      // No accordion parent, just scroll
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      el.focus?.();
      el.classList.add('highlighted');
    }
    this.closeSearchResults();
  }

  closeSearchResults() {
    this.searchedElements = [];
    this.isEmpty = true;
  }

  removeHighlight() {
    const highlightedElements = document.querySelectorAll('.highlighted');
    highlightedElements.forEach((el) => {
      el.classList.remove('highlighted');
    });
  }

  ngOnDestroy(): void {
    this.getSearchedElementSubs$.unsubscribe();
    this.resetPreviousSearchedSubs$.unsubscribe();
  }
}
